/**
 * `src/lib/ui/theme/perceptual.ts` — CIELab, CIEDE2000, and colour-vision
 * simulation. SPEC §1.6 item 8, SPEC §2.1 ("colour must never be the only
 * carrier of meaning").
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY THIS EXISTS: THE HIGHLIGHT TEST WAS CHECKING THAT SIX STRINGS DIFFER
 * ═══════════════════════════════════════════════════════════════════════════
 * `tests/ui/theme.spec.ts` claimed to assert that the six highlight swatches
 * are "distinguishable from each other", and what it actually asserted was
 * `new Set(HIGHLIGHTS.map(colour)).size === 6`. `#f7e9a8` and `#f7e9a9` would
 * have passed. Its own comment named the real problem — "colour is the only
 * thing distinguishing them" — and then did not test for it.
 *
 * Measured on the palette that test was passing:
 *
 *   light, normal vision   closest pair ΔE2000 = 10.5
 *   light, deuteranopia    closest pair ΔE2000 =  0.6   (hl-3 vs hl-5)
 *   dark,  deuteranopia    closest pair ΔE2000 =  1.4   (hl-3 vs hl-5)
 *
 * 2.3 is the usual figure for a just-noticeable difference. 0.6 is the same
 * colour. So for a deuteranope — roughly 1 in 16 men — two of the six highlight
 * choices were the same highlight, and M3 is about to build the highlight menu
 * on those six swatches.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHAT IS AND IS NOT CLAIMED HERE
 * ═══════════════════════════════════════════════════════════════════════════
 * WCAG contrast (`contrast.ts`) answers "can this text be read on this ground".
 * It says NOTHING about whether two colours can be told apart, because it is a
 * function of luminance alone: two swatches with identical luminance and
 * opposite hue have a contrast ratio of 1.00:1 and are obviously different
 * colours. Distinguishability needs a perceptual metric, hence CIEDE2000.
 *
 * The CVD simulation is Viénot, Brettel & Mollon (1999) — the standard
 * linear-RGB projection for dichromacy, and the one every published palette
 * check uses. It models PROTANOPIA and DEUTERANOPIA, which together are the
 * overwhelming majority of colour vision deficiency. It does NOT model
 * tritanopia (rare, and the blue-yellow axis is the one this palette leans on
 * hardest, so simulating it would mostly reproduce the luminance ladder), nor
 * anomalous trichromacy (milder than the dichromat case simulated here, so the
 * dichromat bound is the conservative one).
 *
 * A simulation is a model of a visual system, not a measurement of one. It is
 * enough to catch "these two are literally the same colour" and it is not a
 * substitute for the rule recorded in `M2-STATUS.md`: M3 must never let colour
 * alone carry "there is a note here".
 *
 * Build-time only, like `contrast.ts`. Nothing in the running app calls any of
 * this.
 */

import { parseHex } from './contrast';

/* ═══════════════════ sRGB ⇄ linear ⇄ XYZ ⇄ Lab ═══════════════════════════ */

/**
 * sRGB companding, the IEC 61966-2-1 form (0.04045 / 12.92).
 *
 * DELIBERATELY NOT the 0.03928 threshold `relativeLuminance` uses. That
 * function reproduces the WCAG 2.1 formula VERBATIM because its numbers have to
 * agree with every audit tool a reviewer runs; this one is doing colorimetry,
 * where the sRGB standard's own value is correct. The two differ in the fourth
 * decimal place and the divergence is on purpose, so neither can be "fixed" to
 * match the other.
 */
function toLinear(channel: number): number {
	const c = channel / 255;
	return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function toSrgb(linear: number): number {
	const v = linear < 0 ? 0 : linear > 1 ? 1 : linear;
	const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
	return Math.round(c * 255);
}

function requireRgb(hex: string): [number, number, number] {
	const rgb = parseHex(hex);
	if (rgb === null) throw new Error(`not a hex colour: ${JSON.stringify(hex)}`);
	return rgb;
}

function toHex(rgb: readonly [number, number, number]): string {
	return `#${rgb.map((c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0')).join('')}`;
}

/** D65, the sRGB white point. */
const WHITE = [0.95047, 1.0, 1.08883] as const;

/** CIE L*a*b* under D65. */
export function labOf(hex: string): [number, number, number] {
	const [r, g, b] = requireRgb(hex).map(toLinear) as [number, number, number];
	const x = 0.4124564 * r + 0.3575761 * g + 0.1804375 * b;
	const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
	const z = 0.0193339 * r + 0.119192 * g + 0.9503041 * b;
	const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (841 / 108) * t + 4 / 29);
	const fx = f(x / WHITE[0]);
	const fy = f(y / WHITE[1]);
	const fz = f(z / WHITE[2]);
	return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/* ═══════════════════ CIEDE2000 ═══════════════════════════════════════════ */

const RAD = Math.PI / 180;

/**
 * CIEDE2000 colour difference between two hex colours.
 *
 * Roughly: 1 is invisible, 2.3 is the commonly cited just-noticeable
 * difference, 10 is "obviously a different colour". Written out in full rather
 * than pulled from a dependency because it is forty lines of arithmetic with a
 * published reference implementation to check against, and because a colour
 * library is a dependency this project would otherwise not have.
 */
export function deltaE2000(a: string, b: string): number {
	const [L1, a1, b1] = labOf(a);
	const [L2, a2, b2] = labOf(b);

	const C1 = Math.hypot(a1, b1);
	const C2 = Math.hypot(a2, b2);
	const Cbar = (C1 + C2) / 2;
	const Cbar7 = Math.pow(Cbar, 7);
	const G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + Math.pow(25, 7))));

	const a1p = (1 + G) * a1;
	const a2p = (1 + G) * a2;
	const C1p = Math.hypot(a1p, b1);
	const C2p = Math.hypot(a2p, b2);
	const h1p = a1p === 0 && b1 === 0 ? 0 : (Math.atan2(b1, a1p) / RAD + 360) % 360;
	const h2p = a2p === 0 && b2 === 0 ? 0 : (Math.atan2(b2, a2p) / RAD + 360) % 360;

	const dLp = L2 - L1;
	const dCp = C2p - C1p;
	let dhp: number;
	if (C1p * C2p === 0) dhp = 0;
	else if (Math.abs(h2p - h1p) <= 180) dhp = h2p - h1p;
	else if (h2p - h1p > 180) dhp = h2p - h1p - 360;
	else dhp = h2p - h1p + 360;
	const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * RAD) / 2);

	const Lbar = (L1 + L2) / 2;
	const Cbarp = (C1p + C2p) / 2;
	let hbar: number;
	if (C1p * C2p === 0) hbar = h1p + h2p;
	else if (Math.abs(h1p - h2p) <= 180) hbar = (h1p + h2p) / 2;
	else if (h1p + h2p < 360) hbar = (h1p + h2p + 360) / 2;
	else hbar = (h1p + h2p - 360) / 2;

	const T =
		1 -
		0.17 * Math.cos((hbar - 30) * RAD) +
		0.24 * Math.cos(2 * hbar * RAD) +
		0.32 * Math.cos((3 * hbar + 6) * RAD) -
		0.2 * Math.cos((4 * hbar - 63) * RAD);

	const dTheta = 30 * Math.exp(-Math.pow((hbar - 275) / 25, 2));
	const Cbarp7 = Math.pow(Cbarp, 7);
	const Rc = 2 * Math.sqrt(Cbarp7 / (Cbarp7 + Math.pow(25, 7)));
	const Sl = 1 + (0.015 * Math.pow(Lbar - 50, 2)) / Math.sqrt(20 + Math.pow(Lbar - 50, 2));
	const Sc = 1 + 0.045 * Cbarp;
	const Sh = 1 + 0.015 * Cbarp * T;
	const Rt = -Math.sin(2 * dTheta * RAD) * Rc;

	return Math.sqrt(
		Math.pow(dLp / Sl, 2) +
			Math.pow(dCp / Sc, 2) +
			Math.pow(dHp / Sh, 2) +
			Rt * (dCp / Sc) * (dHp / Sh)
	);
}

/* ═══════════════════ dichromacy simulation ═══════════════════════════════ */

/** The two dichromacies this project simulates; see the header for what is not. */
export type ColourVision = 'normal' | 'protanopia' | 'deuteranopia';

/** Viénot/Brettel/Mollon 1999 projections, in LINEAR sRGB. */
const PROJECTION: Record<Exclude<ColourVision, 'normal'>, readonly (readonly number[])[]> = {
	protanopia: [
		[0.11238, 0.88762, 0.0],
		[0.11238, 0.88762, 0.0],
		[0.00401, -0.00401, 1.0]
	],
	deuteranopia: [
		[0.29275, 0.70725, 0.0],
		[0.29275, 0.70725, 0.0],
		[-0.02234, 0.02234, 1.0]
	]
};

/**
 * What `hex` looks like to a protanope or a deuteranope, as a hex colour.
 *
 * `'normal'` returns the input unchanged, so a caller can loop over all three
 * without branching.
 */
export function simulateColourVision(hex: string, vision: ColourVision): string {
	if (vision === 'normal') return toHex(requireRgb(hex));
	const matrix = PROJECTION[vision];
	const linear = requireRgb(hex).map(toLinear);
	const out = matrix.map((row) =>
		toSrgb(row.reduce((sum, weight, i) => sum + weight * (linear[i] as number), 0))
	) as [number, number, number];
	return toHex(out);
}

/**
 * The closest pair in a set of colours, under one kind of colour vision.
 *
 * Returns the pair as well as the distance, because "the palette fails" is a
 * much less useful test failure than "hl-3 and hl-5 are the same colour to a
 * deuteranope".
 */
export function closestPair(
	colours: readonly string[],
	vision: ColourVision = 'normal'
): { distance: number; a: number; b: number } {
	const simulated = colours.map((c) => simulateColourVision(c, vision));
	let best = { distance: Number.POSITIVE_INFINITY, a: -1, b: -1 };
	for (let i = 0; i < simulated.length; i++) {
		for (let j = i + 1; j < simulated.length; j++) {
			const distance = deltaE2000(simulated[i] as string, simulated[j] as string);
			if (distance < best.distance) best = { distance, a: i, b: j };
		}
	}
	return best;
}
